import { ErrorHandler, Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import * as Sentry from "@sentry/angular";

const CHUNK_FAILED_MESSAGE = /Loading chunk [\d]+ failed/;
const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  handleError(wrapped: unknown): void {
    const error = this.unwrap(wrapped);

    if (error instanceof Error && CHUNK_FAILED_MESSAGE.test(error.message)) {
      window.location.reload();
      return;
    }

    if (error instanceof HttpErrorResponse) {
      this.reportHttpError(error);
    } else if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(this.describeNonError(error), "error");
    }
    console.error(wrapped);
  }

  // zone.js wraps promise rejections; the original error is in `rejection`.
  private unwrap(error: unknown): unknown {
    const rejection = (error as { rejection?: unknown })?.rejection;
    return rejection ?? error;
  }

  private reportHttpError(error: HttpErrorResponse): void {
    // 0 = user connectivity, 4xx = already surfaced by the UI modals.
    if (error.status < 500) {
      return;
    }
    const endpoint = (error.url || "unknown")
      .split("?")[0]
      .replace(UUID_PATTERN, ":id");
    Sentry.captureEvent({
      message: `HTTP ${error.status} ${endpoint}`,
      level: "error",
      fingerprint: [String(error.status), endpoint],
      extra: { url: error.url, body: error.error },
    });
  }

  private describeNonError(value: unknown): string {
    try {
      return `Non-Error thrown: ${JSON.stringify(value)?.slice(0, 500)}`;
    } catch {
      return `Non-Error thrown: ${String(value)}`;
    }
  }
}
